import { useState, useMemo } from "react";
import reviewsApi from "../api/reviews";

export function useTestimonialsModal(announceToScreenReader, { selectedItem, setSelectedItem, isProfileReadOnly }) {
  const [showTestimonialsModal, setShowTestimonialsModal] = useState(false);
  const [showTestimonialsSearch, setShowTestimonialsSearch] = useState(false);
  const [testimonialsSearchQuery, setTestimonialsSearchQuery] = useState("");
  const [testimonialsFilterRating, setTestimonialsFilterRating] = useState("all");

  const [modalTestimonials, setModalTestimonials] = useState(null);
  const [, setModalServiceTitle] = useState("");
  const [modalProviderName, setModalProviderName] = useState("");
  const [modalProviderId, setModalProviderId] = useState(null);
  const [modalRespondingToReviewId, setModalRespondingToReviewId] = useState(null);

  const testimonials = useMemo(() => [], []);

  const currentTestimonials = useMemo(() => {
    return (modalTestimonials !== null && Array.isArray(modalTestimonials) && modalTestimonials.length > 0)
      ? modalTestimonials
      : testimonials;
  }, [modalTestimonials, testimonials]);

  const filteredTestimonials = (Array.isArray(currentTestimonials) ? currentTestimonials : []).filter((testimonial) => {
    if (!testimonial || typeof testimonial !== "object") return false;

    const matchesSearch =
      testimonialsSearchQuery === "" ||
      (testimonial.name && testimonial.name.toLowerCase().includes(testimonialsSearchQuery.toLowerCase())) ||
      (testimonial.comment && testimonial.comment.toLowerCase().includes(testimonialsSearchQuery.toLowerCase()));

    const matchesRating =
      testimonialsFilterRating === "all" ||
      testimonial.rating === parseInt(testimonialsFilterRating);

    return matchesSearch && matchesRating;
  });

  const testimonialsStats = {
    total: Array.isArray(currentTestimonials) ? currentTestimonials.length : 0,
    averageRating:
      Array.isArray(currentTestimonials) && currentTestimonials.length > 0
        ? (currentTestimonials.reduce((sum, t) => sum + (t.rating || 0), 0) / currentTestimonials.length).toFixed(1)
        : "0.0",
    ratingBreakdown: {
      5: Array.isArray(currentTestimonials) ? currentTestimonials.filter((t) => t.rating === 5).length : 0,
      4: Array.isArray(currentTestimonials) ? currentTestimonials.filter((t) => t.rating === 4).length : 0,
      3: Array.isArray(currentTestimonials) ? currentTestimonials.filter((t) => t.rating === 3).length : 0,
      2: Array.isArray(currentTestimonials) ? currentTestimonials.filter((t) => t.rating === 2).length : 0,
      1: Array.isArray(currentTestimonials) ? currentTestimonials.filter((t) => t.rating === 1).length : 0,
    },
  };

  const openTestimonialsModal = (testimonialsData = null, serviceTitle = "", providerName = "", providerId = null) => {
    setTestimonialsFilterRating("all");
    setTestimonialsSearchQuery("");

    if (testimonialsData && Array.isArray(testimonialsData) && testimonialsData.length > 0) {
      setModalTestimonials(testimonialsData);
      setModalServiceTitle(serviceTitle);
      setModalProviderName(providerName);
      setModalProviderId(providerId);
      setTimeout(() => {
        setShowTestimonialsModal(true);
        announceToScreenReader("Opening testimonials modal");
      }, 0);
    } else {
      setModalTestimonials(null);
      setModalServiceTitle("");
      setModalProviderName("");
      setModalProviderId(providerId);
      setShowTestimonialsModal(true);
      announceToScreenReader("Opening testimonials modal");
    }
  };

  /**
   * Post an owner's reply to a review, then show it.
   *
   * The write has to come first. This used to update the on-screen testimonial
   * and nothing else, so a reply looked posted, survived until the next reload,
   * and then vanished — with no error anywhere, because nothing had been asked
   * of the server to fail.
   *
   * ListingDetailsScreen writes its own replies and is never given this as
   * `onResponseSubmit`, so it is not double-posting through here.
   *
   * @returns {{ success: boolean, error?: string }}
   */
  const handleResponseSubmit = async (reviewId, responseText, providerName) => {
    const res = await reviewsApi.respond(reviewId, responseText);
    if (!res?.success) {
      // Nothing is shown on failure: a reply that reads as posted while the
      // database has never heard of it is worse than an obvious no-op.
      announceToScreenReader?.("Your response could not be posted");
      return { success: false, error: res?.error };
    }

    if (modalTestimonials) {
      const updatedTestimonials = modalTestimonials.map((testimonial) => {
        if (testimonial.id === reviewId) {
          return {
            ...testimonial,
            providerResponse: {
              text: responseText,
              date: "Just now",
              providerName: providerName || modalProviderName,
            },
          };
        }
        return testimonial;
      });
      setModalTestimonials(updatedTestimonials);
    }

    if (selectedItem && selectedItem.testimonials) {
      const updatedItemTestimonials = selectedItem.testimonials.map((testimonial) => {
        if (testimonial.id === reviewId) {
          return {
            ...testimonial,
            providerResponse: {
              text: responseText,
              date: "Just now",
              providerName: providerName || modalProviderName,
            },
          };
        }
        return testimonial;
      });
      setSelectedItem({
        ...selectedItem,
        testimonials: updatedItemTestimonials,
      });
    }

    return { success: true };
  };

  const resetTestimonialsModal = () => {
    setShowTestimonialsModal(false);
    setShowTestimonialsSearch(false);
    setTestimonialsSearchQuery("");
    setTestimonialsFilterRating("all");
    setModalTestimonials(null);
    setModalServiceTitle("");
    setModalProviderName("");
    setModalProviderId(null);
    setModalRespondingToReviewId(null);
  };

  return {
    showTestimonialsModal,
    setShowTestimonialsModal,
    showTestimonialsSearch,
    setShowTestimonialsSearch,
    testimonialsSearchQuery,
    setTestimonialsSearchQuery,
    testimonialsFilterRating,
    setTestimonialsFilterRating,
    modalTestimonials,
    modalProviderName,
    modalProviderId,
    modalRespondingToReviewId,
    setModalRespondingToReviewId,
    testimonials,
    currentTestimonials,
    filteredTestimonials,
    testimonialsStats,
    openTestimonialsModal,
    handleResponseSubmit,
    resetTestimonialsModal,
  };
}
